import React, { useState } from 'react';
import axios from 'axios';
import { SuspendedAccount as Suspended } from '../../context/auth/AuthContext';
import { CardFields } from '../features/CardFields';
import '../signup/signup.css';
import { CardForm, cardBrandIcon, cardErrors, cardPayload, emptyCard, formatMoney } from '../features/featureUi';

// Público (sin sesión): usa axios directo; topApi guardaría como token de sesión cualquier respuesta 200
const publicApi = axios.create({ baseURL: process.env.REACT_APP_API_URL });

interface Props {
    account: Suspended;
    onClose: () => void;            // volver al login
}

/**
 * Cuenta suspendida por falta de pago (se muestra al iniciar sesión): tarjeta donde se intentó el cobro (últimos 4)
 * y la opción de registrar otra. Al guardarla se cobra de inmediato; si se aprueba, la cuenta se reactiva.
 */
export const SuspendedAccount = ({ account, onClose }: Props) => {
    const [changing, setChanging] = useState(false);
    const [card, setCard] = useState<CardForm>(emptyCard);
    const [touched, setTouched] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [result, setResult] = useState<{ approved: boolean; message: string; last4: string | null } | null>(null);

    const errors = touched ? cardErrors(card) : {};
    const currentLast4 = result?.last4 ?? account.card?.last4 ?? null;
    const hasCard = !!currentLast4;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (Object.keys(cardErrors(card)).length) { setTouched(true); return; }
        setSaving(true);
        setError('');
        try {
            const resp = await publicApi.post('/merchant-subscription/suspended/card', { billingToken: account.billingToken, card: cardPayload(card) });
            setResult({ approved: !!resp.data?.approved, message: resp.data?.message ?? '', last4: resp.data?.card?.last4 ?? currentLast4 });
            setCard(emptyCard);
            setTouched(false);
            if (resp.data?.approved) setChanging(false);
        } catch (err: any) {
            const data = err?.response?.data;
            setError(data?.error ?? 'No se pudo actualizar la tarjeta. Intente de nuevo.');
            if (err?.response?.status === 401) setTimeout(onClose, 2500);
        } finally {
            setSaving(false);
        }
    };

    if (result?.approved) {
        return (
            <div className="text-center">
                <div className="signup-done-icon mb-3"><i className="bi bi-check-lg" /></div>
                <h5 className="mb-2">¡Pago aprobado!</h5>
                <p className="text-muted small">{result.message || 'Su cuenta está activa nuevamente.'} Cobramos a la tarjeta terminada en <strong>{result.last4}</strong>.</p>
                <button type="button" className="btn btn-primary w-100" onClick={onClose}>
                    <i className="bi bi-box-arrow-in-right me-1" /> Iniciar sesión
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="text-center mb-3">
                <div className="text-danger fs-1 mb-1"><i className="bi bi-exclamation-octagon" /></div>
                <h5 className="mb-1">Cuenta suspendida</h5>
                <p className="text-muted small mb-0">
                    {account.card ? 'No pudimos cobrar su suscripción después de varios intentos.' : 'No pudimos cobrar su suscripción porque no tiene una tarjeta registrada.'}
                </p>
            </div>

            <div className="d-flex align-items-center gap-3 border rounded-3 p-3 mb-3">
                <i className={`bi ${cardBrandIcon(account.card?.brand)} fs-3 ${hasCard ? 'text-primary' : 'text-muted'}`} />
                <div className="flex-grow-1">
                    {hasCard ? (<>
                        <div className="fw-semibold">{account.card?.brand && account.card.brand !== 'OTRA' && !result ? account.card.brand : 'Tarjeta'} •••• {currentLast4}</div>
                        <div className="small text-muted">Tarjeta donde intentamos el cobro</div>
                    </>) : (<>
                        <div className="fw-semibold">Sin tarjeta registrada</div>
                        <div className="small text-muted">Agregue una para pagar y reactivar la cuenta</div>
                    </>)}
                </div>
                <div className="text-end">
                    <div className="fw-semibold">{formatMoney(account.amount, account.currency)}</div>
                    <div className="small text-muted">pendiente</div>
                </div>
            </div>

            {(result && !result.approved) && <div className="alert alert-warning small" role="alert">{result.message}</div>}
            {!result && account.card && account.lastError && <div className="small text-muted mb-3">Motivo del último rechazo: {account.lastError}</div>}
            {error && <div className="alert alert-danger small" role="alert">{error}</div>}

            {changing ? (
                <form onSubmit={submit} noValidate>
                    <h6 className="mb-2">{hasCard ? 'Nueva tarjeta' : 'Agregar tarjeta'}</h6>
                    <CardFields card={card} onChange={setCard} errors={errors} disabled={saving} idPrefix="suspended-card"
                        note="Solo guardamos los últimos 4 dígitos de la tarjeta." />
                    <p className="small text-muted mt-2 mb-0">Al guardarla cobraremos {formatMoney(account.amount, account.currency)} y su cuenta se reactivará de inmediato.</p>
                    <div className="d-flex gap-2 mt-3">
                        <button type="button" className="btn btn-outline-secondary" disabled={saving} onClick={() => { setChanging(false); setTouched(false); }}>
                            Cancelar
                        </button>
                        <button type="submit" className="btn btn-primary flex-grow-1" disabled={saving}>
                            {saving ? <><span className="spinner-border spinner-border-sm me-2" />Procesando pago...</> : <><i className="bi bi-credit-card me-1" /> Guardar y pagar</>}
                        </button>
                    </div>
                </form>
            ) : (
                <button type="button" className="btn btn-primary w-100" onClick={() => { setChanging(true); setError(''); }}>
                    <i className={`bi ${hasCard ? 'bi-arrow-repeat' : 'bi-plus-lg'} me-1`} /> {hasCard ? 'Cambiar tarjeta' : 'Agregar tarjeta'}
                </button>
            )}

            <div className="text-center mt-3">
                <button type="button" className="btn btn-link btn-sm text-decoration-none" onClick={onClose} disabled={saving}>
                    <i className="bi bi-arrow-left me-1" /> Volver al inicio de sesión
                </button>
            </div>
        </div>
    );
};

export default SuspendedAccount;
